import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

interface Props {
  text: string;
  /** 접혔을 때 노출할 라인 수 (기본 2) */
  lines?: number;
  className?: string;
  /** 버튼 색 톤 (기본: primary) */
  toggleColorClass?: string;
}

/**
 * 여러 줄 텍스트를 N라인까지 clamp 하고, 넘칠 때만 더보기/접기 버튼을 노출.
 * - DOM 측정(scrollHeight vs clientHeight)으로 오버플로우 감지
 * - text 변경 / 폰트 로딩 후 리사이즈 관찰
 */
export function ExpandableText({
  text,
  lines = 2,
  className,
  toggleColorClass = 'text-[var(--color-primary)]',
}: Props) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    // clamp 된 상태에서 scrollHeight 가 clientHeight 보다 크면 잘림
    const isOverflow = el.scrollHeight - el.clientHeight > 1;
    setOverflow(isOverflow);
  };

  useLayoutEffect(() => {
    // 펼친 상태에선 측정을 건너뛰어 토글 표시는 유지
    if (expanded) return;
    measure();
  }, [text, lines, expanded]);

  useEffect(() => {
    const onResize = () => {
      if (!expanded) measure();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [expanded]);

  if (!text) return null;

  const clampStyle = !expanded
    ? ({
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical' as const,
        WebkitLineClamp: lines,
        overflow: 'hidden',
      } as const)
    : undefined;

  return (
    <div>
      <p
        ref={ref}
        className={cn('whitespace-pre-wrap break-words', className)}
        style={clampStyle}
      >
        {text}
      </p>
      {overflow && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn('mt-1 text-xs font-medium hover:underline', toggleColorClass)}
        >
          {expanded ? '접기' : '더보기'}
        </button>
      )}
    </div>
  );
}
